import {
  auth,
  LOCAL,
  realtimeDB,
  TIMESTAMP,
  firestore,
  Timestamp,
  storage,
  emailAuthProvider
} from "../_firebase";
import { forIn, isArray, map, isEmpty, isUndefined, forEach } from "lodash";
import Collection from "../_collections";
import { isNull } from "util";
import moment from "moment";
import userConstants from "../_constants/userConstants";
import profileConstants from "../_constants/profileConstants";

const markUserOnlineStatus = () => {
  const uid = auth.currentUser.uid;
  const userStatusDatabaseRef = realtimeDB.ref("/status/" + uid);
  const isOfflineForDatabase = {
    status: "offline",
    last_online: TIMESTAMP
  };

  const isOnlineForDatabase = {
    status: "online",
    last_online: TIMESTAMP
  };

  realtimeDB.ref(".info/connected").on("value", function(snapshot) {
    // If we're not currently connected, don't do anything.
    if (snapshot.val() == false) {
      return;
    }

    // If we are currently connected, then use the 'onDisconnect()'
    // method to add a set which will only trigger once this
    // client has disconnected by closing the app,
    // losing internet, or any other means.
    userStatusDatabaseRef
      .onDisconnect()
      .set(isOfflineForDatabase)
      .then(function() {
        // The promise returned from .onDisconnect().set() will
        // resolve as soon as the server acknowledges the onDisconnect()
        // request, NOT once we've actually disconnected:
        // https://firebase.google.com/docs/reference/js/firebase.database.OnDisconnect

        // We can now safely set ourselves as 'online' knowing that the
        // server will mark us as offline once we lose connection.
        userStatusDatabaseRef.set(isOnlineForDatabase);
      });
  });
};
const getUserInfo = user => {
  if (!user) {
    return null;
  }

  return {
    name: user.displayName,
    email: user.email,
    isEmailVerified: user.emailVerified,
    isAnonymous: user.isAnonymous,
    meta: user.metadata,
    mobile: user.phoneNumber,
    id: user.uid
  };
};
const login = (email, password) => {
  let promise = new Promise((resolve, reject) => {
    auth
      .setPersistence(LOCAL)
      .then(() => {
        auth.signInWithEmailAndPassword(email, password).catch(err => {
          reject({ code: err.code, message: err.message });
        });
      })
      .catch(err => {
        auth.signInWithEmailAndPassword(email, password).catch(err => {
          reject({ code: err.code, message: err.message });
        });
      });
  });
  return promise;
};
const logout = () => {
  let promise = new Promise((resolve, reject) => {
    const userId = auth.currentUser.uid;

    const userStatusDatabaseRef = realtimeDB.ref("/status/" + userId);
    const isOfflineForDatabase = {
      status: "offline",
      last_online: TIMESTAMP
    };
    userStatusDatabaseRef.set(isOfflineForDatabase);
    auth
      .signOut()
      .then(() => {
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
  return promise;
};
const signup = (email, password) => {
  let promise = new Promise((resolve, reject) => {
    auth
      .createUserWithEmailAndPassword(email, password)
      .then(user => {
        //SEND VERIFICATION EMAIL
        //auth.currentUser.sendEmailVerification();
        const userId = auth.currentUser.uid;
        const userStatusDatabaseRef = realtimeDB.ref("/status/" + userId);
        const isOfflineForDatabase = {
          status: "online",
          last_online: TIMESTAMP
        };
        userStatusDatabaseRef.set(isOfflineForDatabase);
        resolve(user);
      })
      .catch(error => {
        reject(error);
      });
  });
  return promise;
};
const DEFAULT_PARTNER_PREFERENCE = {
  ageStart: 21,
  ageEnd: 27,
  maritalStatus: { all: true },
  motherTongue: { all: true },
  complexion: { all: true },
  bodyType: { all: true },
  diet: { all: true },
  smoking: { all: true },
  drinking: { all: true },
  state: { all: true },
  country: { all: true },
  education: { all: true },
  employedIn: { all: true },
  jobType: { all: true },
  income: { all: true }
};
const convertPartnerPreferenceCompatibleToFirestore = preference => {
  if (!preference) {
    return null;
  }
  try {
    let convertedPreference = {};
    forIn(preference, (value, key) => {
      if (typeof value === "number") {
        convertedPreference[key] = value;
      } else if (isArray(value)) {
        if (value.length === 0) {
          // convertedPreference[`${key}.all`] = true;
          convertedPreference[key] = { all: true };
        } else {
          let propertyValue = {};
          map(value, itemValue => {
            // convertedPreference[`${key}.${itemValue.value}`] = true;
            propertyValue[itemValue.value] = true;
          });
          // convertedPreference[`${key}.all`] = false;
          propertyValue.all = false;
          convertedPreference[key] = propertyValue;
        }
      }
    });
    return convertedPreference;
  } catch (error) {
    return null;
  }
};
const updatePartnerPreference = (preference, previousPreference) => {
  let promise = new Promise((resolve, reject) => {
    const updatedPreference = convertPartnerPreferenceCompatibleToFirestore(
      preference
    );
    if (isEmpty(previousPreference)) {
      previousPreference = DEFAULT_PARTNER_PREFERENCE;
    }
    if (updatedPreference === null) {
      reject("Please Try Again Later.");
    } else {
      const userId = auth.currentUser.uid;
      firestore
        .collection(Collection.USERS)
        .doc(userId)
        .collection(Collection.PARTNER_PREFERENCE)
        .doc(userId)
        .set({
          ...previousPreference,
          ...updatedPreference,
          ...{ updated_at: Timestamp.now() }
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    }
  });
  return promise;
};
const updateNameGenderAndCreatedBy = (name = null, isFemale, createdBy) => {
  let promise = new Promise((resolve, reject) => {
    try {
      const user = auth.currentUser;
      const imageMale =
        "https://firebasestorage.googleapis.com/v0/b/fir-amst-8de23.appspot.com/o/placeholder%2Fmale.png?alt=media&token=f37028c3-cb83-4b39-88a7-9eb3d394bd1c";
      const imageFemale =
        "https://firebasestorage.googleapis.com/v0/b/fir-amst-8de23.appspot.com/o/placeholder%2Ffemale.png?alt=media&token=9526c2bb-d143-4749-bc41-1bce7fc1f8c1";
      let updatedObject = {};
      if (isFemale !== undefined) {
        updatedObject = {
          isFemale,
          createdBy,
          name,
          imageURL: isFemale ? imageFemale : imageMale
        };
      } else {
        updatedObject = {
          createdBy,
          name
        };
      }
      firestore
        .collection(Collection.USERS)
        .doc(user.uid)
        .update(updatedObject)
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};
const updateMyInformation = (name, mobile, createdBy) => {
  const userId = auth.currentUser.uid;
  let updateProfile = new Promise((resolve, reject) => {
    try {
      firestore
        .collection(Collection.USERS)
        .doc(userId)
        .update({
          createdBy,
          name
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  let updatePhoneNumber = new Promise((resolve, reject) => {
    try {
      firestore
        .collection(Collection.USERS)
        .doc(userId)
        .collection(Collection.PHONE_NUMBER)
        .doc(userId)
        .set({
          mobile
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return Promise.all([updateProfile, updatePhoneNumber])
    .then(() => {
      return true;
    })
    .catch(err => {
      return Promise.reject(err);
    });
};

const convertUserProfileCompatibleToFirestore = userProfile => {
  if (!userProfile) {
    return null;
  }
  try {
    let convertedUserProfile = {};
    forIn(userProfile, (value, key) => {
      if (key === "marriedBrother" || key === "marriedSister") {
        if (value !== null) {
          convertedUserProfile[key] = value.value;
        } else {
          convertedUserProfile[key] = 0;
        }
      } else if (key === "birthDate") {
        if (value !== null) {
          convertedUserProfile[key] = value;
          convertedUserProfile["age"] = moment().diff(value, "years");
        }
      } else if (key === "age") {
        convertedUserProfile[key] = value;
      } else if (key === "information") {
        convertedUserProfile[key] = value;
      } else if (key === "address" || key === "birthPlace") {
        if (value === null || typeof value === "string") {
          //nothing
        } else {
          let propertyValue = {};
          forIn(value, (nestedValue, nestedKey) => {
            if (nestedValue === undefined) {
              propertyValue[nestedKey] = null;
            } else {
              propertyValue[nestedKey] = nestedValue;
            }
          });

          convertedUserProfile[key] = propertyValue;
        }
      } else {
        if (value !== null) {
          convertedUserProfile[key] = value.value;
        }
      }
    });
    return convertedUserProfile;
  } catch (error) {
    return null;
  }
};
const updateUserProfile = userProfile => {
  let promise = new Promise((resolve, reject) => {
    const updatedUserProfile = convertUserProfileCompatibleToFirestore(
      userProfile
    );
    if (updatedUserProfile === null) {
      reject("Please Try Again Later");
    } else {
      const userId = auth.currentUser.uid;
      firestore
        .collection(Collection.USERS)
        .doc(userId)
        .update(updatedUserProfile)
        .then(() => {
          resolve("Please Try Again Later");
        })
        .catch(() => {
          reject("Please Try Again Later");
        });
    }
  });
  return promise;
};
const updatePrivacy = privacy => {
  let promise = new Promise((resolve, reject) => {
    if (isEmpty(privacy)) {
      reject("Please Try Again Later");
    }

    const userId = auth.currentUser.uid;
    firestore
      .collection(Collection.USERS)
      .doc(userId)
      .collection(Collection.PRIVACY_SETTING)
      .doc(userId)
      .update(privacy)
      .then(() => {
        resolve();
      })
      .catch(() => {
        reject("Please Try Again Later");
      });
  });
  return promise;
};
const updateProfilePicture = file => {
  let promise = new Promise((resolve, reject) => {
    try {
      // Create the file metadata
      const metadata = {
        contentType: "image/jpeg"
      };

      // Upload file and metadata to the object 'images/mountains.jpg'
      storage
        .child(`profile/${auth.currentUser.uid}/${file.name}`)
        .put(file, metadata)
        .then(snapshot => {
          snapshot.ref
            .getDownloadURL()
            .then(url => {
              resolve(url);
            })
            .catch(err => {
              reject("Please Try Again Later.");
            });
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};

const removeProfilePicture = isFemale => {
  let promise = new Promise((resolve, reject) => {
    try {
      firestore
        .collection(Collection.USERS)
        .doc(auth.currentUser.uid)
        .update({
          imageURL: isFemale
            ? userConstants.FEMALE_IMAGE
            : userConstants.MALE_IMAGE
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};
const getTopConnected = profileId => {
  let promise = new Promise((resolve, reject) => {
    firestore
      .collection(Collection.USERS)
      .where("createdAt", ">=", moment().subtract(7,'days').utc().toDate())
      .orderBy("createdAt", "desc")
      .get()
      .then(results => {
        let connectedResults = [];
        results.forEach(result => {
          connectedResults.push({ ...result.data(), ...{ id: result.id } });
        });
        resolve(connectedResults);
      })
      .catch(err => {
        console.log(err);
        reject("Please Try Again Later");
      });
  });
  return promise;
};

const uploadImages = files => {
  forEach(files, file => {
    // Create the file metadata
    const metadata = {
      contentType: "image/jpeg"
    };

    // Upload file and metadata to the object 'images/mountains.jpg'

    storage
      .child(`images/${auth.currentUser.uid}/${file.name}`)
      .put(file, metadata);
  });
};
const changeProfilePicture = url => {
  let promise = new Promise((resolve, reject) => {
    const userId = auth.currentUser.uid;
    try {
      firestore
        .collection(Collection.USERS)
        .doc(userId)
        .update({
          imageURL: url
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};
const deleteImages = images => {
  const userId = auth.currentUser.uid;
  let promises = [];

  forEach(images, image => {
    let promise = new Promise((resolve, reject) => {
      firestore
        .collection(Collection.USERS)
        .doc(userId)
        .collection(Collection.GALLERY)
        .doc(image.id)
        .delete()
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    });
    promises.push(promise);
  });

  return Promise.all(promises)
    .then(() => {
      return true;
    })
    .catch(err => {
      return Promise.reject(err);
    });
};
const deleteMe = url => {
  let promise = new Promise((resolve, reject) => {
    try {
      firestore
        .collection(Collection.USERS)
        .doc(auth.currentUser.uid)
        .update({
          age: 0
        })
        .then(() => {
          auth.currentUser
            .delete()
            .then(() => {
              resolve();
            })
            .catch(err => {
              reject("Please Try Again Later.");
            });
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};
const reAuthenticate = (email, password) => {
  let promise = new Promise((resolve, reject) => {
    try {
      const credential = emailAuthProvider.credential(email, password);
      auth.currentUser
        .reauthenticateAndRetrieveDataWithCredential(credential)
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};
const resetPassword = password => {
  let promise = new Promise((resolve, reject) => {
    try {
      auth.currentUser
        .updatePassword(password)
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};
const forgotPassword = email => {
  let promise = new Promise((resolve, reject) => {
    try {
      auth
        .sendPasswordResetEmail(email)
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject("Please Try Again Later.");
        });
    } catch (error) {
      reject("Please Try Again Later.");
    }
  });
  return promise;
};
const userService = {
  getUserInfo,
  login,
  logout,
  signup,
  markUserOnlineStatus,
  updatePartnerPreference,
  updateNameGenderAndCreatedBy,
  updateMyInformation,
  updateUserProfile,
  updatePrivacy,
  updateProfilePicture,
  removeProfilePicture,
  getTopConnected,
  uploadImages,
  changeProfilePicture,
  deleteImages,
  deleteMe,
  reAuthenticate,
  resetPassword,
  forgotPassword
};
export default userService;



// WEBPACK FOOTER //
// ./src/_services/userService.js